// Every query and mutation of the cloud sandboxes and their previews. Keys
// are written here only.
//
// - The person's sandboxes: ["me", "cloud"] (the person's own, gone on sign
//   out). `cloud.status` reads it again (part.tsx).
// - The previews the person may see: ["cloud", "previews"] (`preview.list`).
// - The operator's list: ["cloud", "admin", "cloud"] (no event reaches the
//   operator; it is read again after each change and when opened).

import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AdminCloudResponse,
  AdminSandboxDTO,
  MeCloudResponse,
  PreviewListResponse,
  PreviewTicketResponse,
  SandboxProfile,
} from "@trameria/cloud-api/sandboxes-shared";
import { type Api, useApi } from "@trameria/ui-core";

export const meCloudKey = ["me", "cloud"] as const;

export const meCloudQuery = (api: Api) =>
  queryOptions({
    queryKey: meCloudKey,
    queryFn: () => api<MeCloudResponse>("/api/me/cloud"),
  });

/** The person's device list (Me's agents part reads it). */
const devicesKey = ["me", "agents", "devices"] as const;

/** Reads the person's sandboxes and devices again (a sandbox came or went). */
export function refreshSandboxes(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: meCloudKey }),
    qc.invalidateQueries({ queryKey: devicesKey }),
  ]);
}

// ---- previews ----

export const previewsKey = ["cloud", "previews"] as const;

export const previewListQuery = (api: Api) =>
  queryOptions({
    queryKey: previewsKey,
    queryFn: () => api<PreviewListResponse>("/api/preview/list"),
  });

export interface PreviewStopAnswer {
  /** requested: the sandbox stops it on its next report; offline: nothing runs. */
  status: "requested" | "offline";
}

export function useStopPreview() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sandbox: string; name: string }) =>
      api<PreviewStopAnswer>("/api/preview/stop", {
        method: "POST",
        body: input,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: previewsKey }),
  });
}

/** The one-minute ticket to open a preview (never cached: one use only). */
export const previewTicket = (
  api: Api,
  input: { host: string; path: string },
) =>
  api<PreviewTicketResponse>("/api/preview/ticket", {
    method: "POST",
    body: input,
  });

// ---- operators ----

export const adminCloudKey = ["cloud", "admin", "cloud"] as const;

export const adminCloudQuery = (api: Api) =>
  queryOptions({
    queryKey: adminCloudKey,
    queryFn: () => api<AdminCloudResponse>("/api/admin/cloud"),
  });

export function useAllowSandbox() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; profile: SandboxProfile }) =>
      api<AdminSandboxDTO>("/api/admin/cloud", {
        method: "POST",
        body: input,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: adminCloudKey }),
  });
}

export function useRevokeSandbox() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; profile: SandboxProfile }) =>
      api(
        `/api/admin/cloud/${encodeURIComponent(input.userId)}/${input.profile}`,
        { method: "DELETE" },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: adminCloudKey }),
  });
}
